import Button from "../../../../lib/html5-software-library/src/ranj/components/buttons/Button";
import Slice9Button from "../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Button";
import TextUtil from "../../../../lib/html5-software-library/src/ranj/components/text/TextUtil";
import TextWrap from "../../../../lib/html5-software-library/src/ranj/components/text/TextWrap";
import Anchor from "../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import FillArea from "../../../../lib/html5-software-library/src/ranj/layout/FillArea";
import LayoutManager from "../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import StageEvent from "../../../../lib/html5-software-library/src/ranj/performance/StageEvent";
import ScreenNavigatorEvents from "../../../../lib/html5-software-library/src/ranj/ui/ScreenNavigatorEvents";
import CacheUtil from "../../../../lib/html5-software-library/src/ranj/util/CacheUtil";
import AbstractContextObject from "../application/AbstractContextObject";
import ClickEaterFactory from "./ClickEaterFactory";
import SkinFactory from "./SkinFactory";
import SkinKey from "./SkinKey";
import TextFitter from "./text/TextFitter";

/**
 * AbstractMediator.js
 * Extends AbstractContextObject
 * 
 * Contains convenience methods for the following:
 * - managing buttons
 * - managing skins + their mediators
 * 
 * @author Reinier Feijen for Ranj
 **/
export default class AbstractMediator extends AbstractContextObject {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory) {
        super(dispatcher);

        this._target = target;

        this._layoutManager = layoutManager;
        this._clickEaterFactory = clickEaterFactory;

        this._anchoredObjects = {};

        this._clickThroughPreventionLayer = null;
        this._interactionPreventionLayer = null;
    }

    initialize() {
        //workaround for the issue that movieclip's children are not populated instantly (mcs in asset js contain '.wait(1)' for some reason)
        if (this._target.gotoAndStop != null) {
            this._target.gotoAndStop(0);
        }
    }

    destroy() {
        //Unregister all anchored objects
        for (const id in this._anchoredObjects) {
            this._layoutManager.deregisterObject(this._anchoredObjects[id]);
        }

        //Destroy all buttons that were created using createButton.
        if (this._buttons) {
            for (var i = 0; i < this._buttons.length; i++) {
                this._buttons[i].destroy();
            }
        }

        //Remove all displayobjects that were added to a stage using addDisplayObjectToStage
        if (this._displayObjectsAddedToStage) {
            for (var i = 0; i < this._displayObjectsAddedToStage.length; i++) {
                this._dispatcher.dispatch(StageEvent.REMOVE_FROM_STAGE, { asset: this._displayObjectsAddedToStage[i] });
            }
        }

        //Destroy all click eater
        if (this._clickThroughPreventionLayer) {
            this._clickEaterFactory.destroyClickEater(this._clickThroughPreventionLayer);
            delete this._clickThroughPreventionLayer;
        }
        if (this._interactionPreventionLayer) {
            this._clickEaterFactory.destroyClickEater(this._interactionPreventionLayer);
            delete this._interactionPreventionLayer;
        }

        //Kill all delayed calls
        this.killDelayedCalls();

        //Remove all registered listeners
        if (this.handlers && this._dispatcher) {
            for (var type in this.handlers) {
                for (var i = 0; i < this.handlers[type].length; i++) {
                    this._dispatcher.removeEventListener(type, this.handlers[type][i]);
                }
            }
        }
    }

    /**
     * Removes all click listeners from all button created with the `.createButton` method.
     * This comes in handy against preventing spam clicking.
     */
    removeClickListenersFromAllButtons() {
        if (this._buttons) {
            for (let i = 0; i < this._buttons.length; i++) {
                this._buttons[i].removeAllEventListeners("click");
            }
        }
    }

    /**
     * Gets custom skin data.
     * @returns {Object} Object containing the custom skin data.
     */
    getCustomData() {
        return this._target.customData || {};
    }

    /**
     * Adds custom skin data. Adding an already existing property will overwrite it.
     * @param {Object<String, *>} keyValuePairs Object that will be merged with the current custom data
     * @returns {Object} Object containing the custom skin data.
     */
    addCustomData(keyValuePairs) {
        this._target.customData = Object.assign(this._target.customData || {}, keyValuePairs);
    }

    /**
     * Convenience method for adding context listeners.
     * Listeners added with this method will be automatically removed in destroy()
     * Requires: this._dispatcher
     * @param {String} type
     * @param {Function} handler Bound handler!
     */
    registerEventListener(type, handler) {
        //store reference to handler so we can remove it in destroy()
        this._dispatcher.addEventListener(type, handler);

        let handlersForType = this._getHandlersForType(type);
        handlersForType.push(handler);
    }

    unregisterEventListener(type, handler) {
        this._dispatcher.removeEventListener(type, handler);

        const handlersForType = this._getHandlersForType(type);
        for (let i = handlersForType.length - 1; i >= 0; i--) {
            if (handlersForType[i] == handler) {
                handlersForType.splice(i, 1);
            }
        }

        if (handlersForType.length === 0) {
            delete this.handlers[type];
        }
    }

    _getHandlersForType(type) {
        if (!this.handlers) {
            this.handlers = {};
        }

        if (!this.handlers[type]) {
            this.handlers[type] = [];
        }

        return this.handlers[type];
    }

    /**
     * Convenience method for delayed function calls
     * Uses: createjs.Tween
     * We use Tween for this instead of setTimeout, because Tween can be paused
     * @param {Function} handler Bound handler
     * @param {Number} delay Delay in msecs
     * @param {Object} scope Optional scope ('this') to bind handler with. Default this.
     * @return {Object} Tween target
     */
    callDelayed(callback, delay, params, scope) {
        if (!this._delayedCalls) {
            this._delayedCalls = [];
        }
        scope = scope || this;

        const target = {};
        createjs.Tween.get(target).wait(delay).call(callback, params, scope).call(this._unregisterDelayedCall, [target], this);
        this._delayedCalls.push(target);

        return target;
    }

    killDelayedCalls() {
        if (!this._delayedCalls) {
            return;
        }

        for (let i = 0; i < this._delayedCalls.length; i++) {
            createjs.Tween.removeTweens(this._delayedCalls[i]);
        }
        this._delayedCalls.length = 0;
    }

    killDelayedCall(target) {
        createjs.Tween.removeTweens(target);
        this._unregisterDelayedCall(target);
    }

    _unregisterDelayedCall(target) {
        if (!this._delayedCalls) {
            return;
        }

        const idx = this._delayedCalls.indexOf(target);
        if (idx > -1) {
            this._delayedCalls.splice(idx, 1);
        }
    }

    /**
     * Convenience method for adding skins and their mediators
     * Needs:
     *  this._dispatcher
     *  this._target reference to skin
     * NB:
     * We also need to call EVENT_REMOVED_FROM_STAGE to destroy and remove mediator\22
     *  See: removeChildFromParent
     * @param {SkinKey} skinType
     * @param {Container} parent Optional parent container. When undefined, this._target will be used.
     * @param {Object} data Optional object whose properties will be copied to newly created skin.
     * @returns {type} skin
     */
    addChildOfType(skinType, parent, data) {
        //Create the skin using the SkinFactory
        const skin = SkinFactory.getSkin(skinType);

        //Set skinType on skin, if not already set by SkinFactory
        if (!skin.skinType) {
            skin.skinType = skinType;
        }

        //Add to target/parent
        return this.addSkin(skin, parent, data);
    }

    /**
     * Convenience method for adding skins and their mediators
     * Requires: this._dispatcher, this._target
     * Uses: ranj.ScreenNavigatorEvents
     * NB:
     * To clean up, we need to call REMOVED_FROM_STAGE to destroy and remove mediator
     *  See: removeChildFromParent
     * @param {DisplayObject} skin 
     * @param {Container} parent Optional parent container. When undefined, this._target will be used.
     * @param {Object} data Optional object whose properties will be copied to newly created skin.
     * @returns {type} skin
     */
    addSkin(skin, parent, data) {
        //Add to target/parent
        if (!parent) {
            parent = this._target;
        }
        parent.addChild(skin);

        //Copy data properties to skin
        if (data) {
            for (let s in data) {
                skin[s] = data[s];
            }
        }

        //Create the mediator, see ranj.MediatorManager.registerMediator
        this._dispatcher.dispatch(ScreenNavigatorEvents.ADDED_TO_STAGE, { data: skin });

        return skin;
    }

    removeChildFromParent(skin) {
        this._dispatcher.dispatch(ScreenNavigatorEvents.REMOVED_FROM_STAGE, { data: skin });

        if (skin.parent) {
            skin.parent.removeChild(skin);
        }
    }

    stretchBackground(bg, bounds) {
        bg = bg || this._target.bg;

        let b = bounds != null ? bounds : bg.nominalBounds;
        this.anchorObject(bg, Anchor.C, new FillArea(b, FillArea.PROPORTIONAL_OUTSIDE));
    }

    /**
     * Convenience method for registering display objects to layout manager.
     * Objects will automatically be deregistered on destroy,
     * or can be deregistered manually by calling _unanchor(target)
     */
    anchorObject(target, anchor, fillArea) {
        this._layoutManager.registerObject(target, anchor, fillArea);
        this._anchoredObjects[target.id] = target;
    }

    unAnchorObject(target, resetPosition = false) {
        this._layoutManager.deregisterObject(target, resetPosition);
        delete this._anchoredObjects[target.id];
    }

    /**
     * Convenience method for adding skins/children to different stages
     * Used in combination with StageManager/StageCommand scheme
     * Uses: ranj.StageEvent
     * @param {DisplayObject} skin 
     * @param {String} stageId from StageID, stage to add skin to.
     * @returns
     **/
    addDisplayObjectToStage(skin, stageId) {
        //store skin so we can remove it when destroying
        if (!this._displayObjectsAddedToStage)
            this._displayObjectsAddedToStage = [];
        if (!this._displayObjectsAddedToStage.contains(skin))
            this._displayObjectsAddedToStage.push(skin);
        //
        this._dispatcher.dispatch(ranj.StageEvent.ADD_TO_STAGE, { asset: skin, stageID: stageId });
    }


    /**
     * Convenience method for caching a displayobject
     * Uses: CacheUtil
     * 
     * @param {DisplayObject} skin
     * @param {Number} marginH Optional horizontal margin, for instance for accomodating blurs
     * @param {Number} marginV Optional vertical margin, for instance for accomodating blurs 
     * @returns {undefined}
     */
    cache(skin, marginH, marginV) {
        CacheUtil.cache(skin, marginH, marginV);
    }

    uncache(skin) {
        CacheUtil.uncache(skin);
    }



    /**
     * Convenience method for creating a Button
     * @param {DisplayObject} skin
     * @param {Function} boundClickHandler Optional handler method for click event.
     * @param {String} label Optional text label.
     * @param {type} clickSoundId Optional sound id to play on click.
     * @returns {AbstractMediator_L12.ranj.Button|AbstractMediator.prototypeObject@call;create.createButton.button}
     */
    createButton(skin, boundClickHandler, label, clickSoundId) {
        const button = new Button(skin);
        this._decorateButton(button, boundClickHandler, label, clickSoundId);

        return button;
    }

    /**
     * Convenience method for creating a Slice9Button
     * @param {DisplayObject} skin
     * @param {Function} boundClickHandler Optional handler method for click event.
     * @param {String} label Optional text label.
     * @param {type} clickSoundId Optional sound id to play on click.
     * @returns {AbstractMediator_L12.ranj.Button|AbstractMediator.prototypeObject@call;create.createButton.button}
     */
    createSlice9Button(skin, boundClickHandler, label, clickSoundId) {
        var button = new Slice9Button(skin, Slice9Button.VERTICAL);
        this._decorateButton(button, boundClickHandler, label, clickSoundId);
        return button;
    }

    _decorateButton(button, boundClickHandler, label, clickSoundId) {
        if (boundClickHandler != null) {
            button.addEventListener("click", boundClickHandler);
        }
        if (label) {
            button.setLabel(label);
        }
        if (clickSoundId) {
            button.addEventListener("click", function () {
                this.playSound(clickSoundId);
            }.bind(this));
        }


        // TODO: not sure if 'decorate' should be responsible for this
        if (!this._buttons) this._buttons = [];
        this._buttons.push(button);
    }

    preventClickThrough() {
        this._removeClickThroughPreventionLayer();  // clean up any previously existing click eater

        this._clickThroughPreventionLayer = this._clickEaterFactory.applyClickEaterLayerTo(this._target, false);
    }

    allowClickThrough() {
        this._removeClickThroughPreventionLayer();
    }

    _removeClickThroughPreventionLayer() {
        if (!this._clickThroughPreventionLayer) {
            return;
        }

        this._clickEaterFactory.destroyClickEater(this._clickThroughPreventionLayer);
        this._clickThroughPreventionLayer = null;
    }

    disableInteraction() {
        this._removeInteractionPreventionLayer(); // clean up any previously existing click eater

        this._interactionPreventionLayer = this._clickEaterFactory.applyClickEaterLayerTo(this._target, true);
    }

    enableInteraction() {
        this._removeInteractionPreventionLayer();
    }

    _removeInteractionPreventionLayer() {
        if (!this._interactionPreventionLayer) {
            return;
        }

        this._clickEaterFactory.destroyClickEater(this._interactionPreventionLayer);
        this._interactionPreventionLayer = null;
    }

    /**
     * Convenience method to apply forceful text wrapping to the given createjs.Text
     * 
     * Note: this operation is destructive to the text in the createjs.Text as it alters the string.
     * 
     * @param {createjs.TextField} textField 
     */
    wrapTextFieldWords(...textFields) {
        textFields.forEach(t => TextWrap.wrapTextField(t));
    }

    /**
     * Sets text to createjs.Text while making it fit the original height defined in the asset and vertically align it
     * 
     * Note: This operation relies in the createjs.Text containing the `originalMeasuredHeight` property set by the SkinFactory
     * Note: this operation caches the TextFitter into the createjs.Text.fitter property
     * 
     * @param {createjs.Text} textField 
     * @param {string} text 
     * @param {object} options
     * @returns {TextFitter} the TextFitter for the given createjs.text
     */
     setAndFitText(textField, text, alignVertically = false, fitOptions = TextFitter.DEFAULT_OPTIONS) {
        if(!textField.fitter) {
            textField.fitter = new TextFitter(textField, fitOptions);
        }
        textField.fitter.setText(text);
        if(alignVertically){
            TextUtil.centerTextVertically(textField, textField.original.y, textField.original.measuredHeight);
        }
        return textField.fitter;
    }
}